import React, { useEffect, useMemo, useRef, useState } from 'react';
import { message } from 'antd';
import api from '../../../system/client/src/services/api';
import PageShell from '../../Components/PageShell';
import Modal from '../../Components/Modal';
import HsCodeTree, { useHsTree, buildCodeLabelMap } from '../../Components/HsCodeTree';
import './CommodityCodesPage.css';

const CommodityCodesPage = () => {
    const tree = useHsTree();
    const labelMap = useMemo(() => buildCodeLabelMap(tree), [tree]);

    const [lists, setLists] = useState([]);
    const [listsLoaded, setListsLoaded] = useState(false);
    const [selection, setSelection] = useState([]);
    const [query, setQuery] = useState('');

    const [addMenuOpen, setAddMenuOpen] = useState(false);
    const addMenuRef = useRef(null);

    const [newListOpen, setNewListOpen] = useState(false);
    const [newListName, setNewListName] = useState('');
    const [savingList, setSavingList] = useState(false);

    const [expandedId, setExpandedId] = useState(null);
    const [renamingId, setRenamingId] = useState(null);
    const [renameValue, setRenameValue] = useState('');
    const [confirmDeleteId, setConfirmDeleteId] = useState(null);

    const fetchLists = async () => {
        try {
            const data = await api.get('commodity-lists');
            setLists(data || []);
        } catch (e) {
            console.error('Error fetching commodity lists:', e);
            message.error('Failed to load your lists');
        } finally {
            setListsLoaded(true);
        }
    };

    useEffect(() => {
        fetchLists();
    }, []);

    useEffect(() => {
        if (!addMenuOpen) return;
        const onClick = (e) => {
            if (addMenuRef.current && !addMenuRef.current.contains(e.target)) {
                setAddMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', onClick);
        return () => document.removeEventListener('mousedown', onClick);
    }, [addMenuOpen]);

    const codeLabel = (code) => {
        const name = labelMap.get(code);
        return name ? `${code} · ${name}` : code;
    };

    const addSelectionToList = async (list) => {
        setAddMenuOpen(false);
        const merged = [...new Set([...(list.codes || []), ...selection])];
        const added = merged.length - (list.codes || []).length;
        if (added === 0) {
            message.info(`All selected codes are already in “${list.name}”`);
            setSelection([]);
            return;
        }
        try {
            const { list: updated } = await api.put(`commodity-lists/${list.id}`, { codes: merged });
            setLists((ls) => ls.map((l) => (l.id === list.id ? updated : l)));
            setSelection([]);
            setExpandedId(list.id);
            message.success(`Added ${added} code${added === 1 ? '' : 's'} to “${list.name}”`);
        } catch (e) {
            message.error(e?.response?.data?.error || 'Failed to update list');
        }
    };

    const createList = async () => {
        const name = newListName.trim();
        if (!name) return;
        setSavingList(true);
        try {
            const { list } = await api.post('commodity-lists', { name, codes: selection });
            setLists((ls) => [...ls, list].sort((a, b) => a.name.localeCompare(b.name)));
            setSelection([]);
            setNewListOpen(false);
            setNewListName('');
            setExpandedId(list.id);
            message.success(
                selection.length
                    ? `Created “${name}” with ${selection.length} code${selection.length === 1 ? '' : 's'}`
                    : `Created “${name}”`,
            );
        } catch (e) {
            message.error(e?.response?.data?.error || 'Failed to create list');
        } finally {
            setSavingList(false);
        }
    };

    const removeCode = async (list, code) => {
        const codes = (list.codes || []).filter((c) => c !== code);
        try {
            const { list: updated } = await api.put(`commodity-lists/${list.id}`, { codes });
            setLists((ls) => ls.map((l) => (l.id === list.id ? updated : l)));
        } catch (e) {
            message.error(e?.response?.data?.error || 'Failed to update list');
        }
    };

    const startRename = (list) => {
        setRenamingId(list.id);
        setRenameValue(list.name);
        setConfirmDeleteId(null);
    };

    const saveRename = async (list) => {
        const name = renameValue.trim();
        setRenamingId(null);
        if (!name || name === list.name) return;
        try {
            const { list: updated } = await api.put(`commodity-lists/${list.id}`, { name });
            setLists((ls) =>
                ls
                    .map((l) => (l.id === list.id ? updated : l))
                    .sort((a, b) => a.name.localeCompare(b.name)),
            );
        } catch (e) {
            message.error(e?.response?.data?.error || 'Failed to rename list');
        }
    };

    const deleteList = async (list) => {
        setConfirmDeleteId(null);
        try {
            await api.delete(`commodity-lists/${list.id}`);
            setLists((ls) => ls.filter((l) => l.id !== list.id));
            if (expandedId === list.id) setExpandedId(null);
            message.success(`Deleted “${list.name}”`);
        } catch (e) {
            message.error(e?.response?.data?.error || 'Failed to delete list');
        }
    };

    const sidebar = (
        <div className="dash-side-scroll">
            <div className="group">
                <span className="group-label">Your lists</span>
                {!listsLoaded ? (
                    <span className="ccl-empty">Loading…</span>
                ) : lists.length === 0 ? (
                    <span className="ccl-empty">
                        No lists yet. Tick codes on the right, then save them as a named
                        list — your whole organisation shares these.
                    </span>
                ) : (
                    lists.map((list) => {
                        const open = expandedId === list.id;
                        return (
                            <div key={list.id} className={`ccl-list${open ? ' open' : ''}`}>
                                <div className="ccl-list-head">
                                    {renamingId === list.id ? (
                                        <input
                                            type="text"
                                            className="ccl-rename-input"
                                            value={renameValue}
                                            autoFocus
                                            onChange={(e) => setRenameValue(e.target.value)}
                                            onBlur={() => saveRename(list)}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') saveRename(list);
                                                if (e.key === 'Escape') setRenamingId(null);
                                            }}
                                        />
                                    ) : (
                                        <button
                                            type="button"
                                            className="ccl-list-name"
                                            onClick={() => {
                                                setExpandedId(open ? null : list.id);
                                                setConfirmDeleteId(null);
                                            }}
                                        >
                                            <span className={`ccl-caret${open ? ' open' : ''}`}>▸</span>
                                            {list.name}
                                            <span className="ccl-list-count">
                                                {(list.codes || []).length}
                                            </span>
                                        </button>
                                    )}
                                </div>
                                {open && (
                                    <div className="ccl-list-body">
                                        {(list.codes || []).length === 0 ? (
                                            <span className="ccl-empty">
                                                Empty — tick codes and add them here.
                                            </span>
                                        ) : (
                                            <div className="tags">
                                                {[...list.codes]
                                                    .sort()
                                                    .map((code) => (
                                                        <span key={code} className="tag-chip muted">
                                                            <span className="commodity-pill-text">
                                                                {codeLabel(code)}
                                                            </span>
                                                            <button
                                                                type="button"
                                                                aria-label={`Remove ${code}`}
                                                                onClick={() => removeCode(list, code)}
                                                            >
                                                                ×
                                                            </button>
                                                        </span>
                                                    ))}
                                            </div>
                                        )}
                                        <div className="ccl-list-actions">
                                            <button
                                                type="button"
                                                className="ccl-link-btn"
                                                onClick={() => startRename(list)}
                                            >
                                                Rename
                                            </button>
                                            {confirmDeleteId === list.id ? (
                                                <>
                                                    <button
                                                        type="button"
                                                        className="ccl-link-btn danger"
                                                        onClick={() => deleteList(list)}
                                                    >
                                                        Confirm delete
                                                    </button>
                                                    <button
                                                        type="button"
                                                        className="ccl-link-btn"
                                                        onClick={() => setConfirmDeleteId(null)}
                                                    >
                                                        Cancel
                                                    </button>
                                                </>
                                            ) : (
                                                <button
                                                    type="button"
                                                    className="ccl-link-btn danger"
                                                    onClick={() => setConfirmDeleteId(list.id)}
                                                >
                                                    Delete
                                                </button>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })
                )}
            </div>
            <div className="dash-side-actions">
                <button
                    type="button"
                    className="btn btn-secondary btn-block"
                    onClick={() => {
                        setNewListName('');
                        setNewListOpen(true);
                    }}
                >
                    + New list
                </button>
            </div>
        </div>
    );

    return (
        <PageShell sidebar={sidebar}>
            <div className="page-head">
                <h1>Commodity codes.</h1>
                <p>
                    Browse HS chapters and headings, and save the codes your organisation
                    specialises in as named lists — then apply them in one click when
                    filtering traders.
                </p>
            </div>

            <div className="ccl-picker">
                <input
                    type="text"
                    className="commodity-picker-search"
                    placeholder="Search codes or descriptions… (e.g. 09, coffee, 8471)"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                />
                <HsCodeTree
                    value={selection}
                    onChange={setSelection}
                    query={query}
                    maxHeight="58vh"
                />
            </div>

            {selection.length > 0 && (
                <div className="ccl-actionbar">
                    <span className="ccl-actionbar-label">
                        {selection.length} code{selection.length === 1 ? '' : 's'} selected
                    </span>
                    <div className="ccl-add-wrap" ref={addMenuRef}>
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={() => setAddMenuOpen((o) => !o)}
                        >
                            Add to list ▾
                        </button>
                        {addMenuOpen && (
                            <div className="ccl-add-menu">
                                {lists.map((list) => (
                                    <button
                                        key={list.id}
                                        type="button"
                                        className="ccl-add-menu-item"
                                        onClick={() => addSelectionToList(list)}
                                    >
                                        {list.name}
                                        <span className="ccl-list-count">
                                            {(list.codes || []).length}
                                        </span>
                                    </button>
                                ))}
                                <button
                                    type="button"
                                    className="ccl-add-menu-item new"
                                    onClick={() => {
                                        setAddMenuOpen(false);
                                        setNewListName('');
                                        setNewListOpen(true);
                                    }}
                                >
                                    + New list…
                                </button>
                            </div>
                        )}
                    </div>
                    <button
                        type="button"
                        className="btn btn-secondary"
                        onClick={() => setSelection([])}
                    >
                        Clear
                    </button>
                </div>
            )}

            <Modal
                open={newListOpen}
                onClose={() => setNewListOpen(false)}
                title="New list"
                width="420px"
            >
                <div className="ccl-new-list-modal">
                    <p className="ccl-modal-hint">
                        {selection.length
                            ? `The ${selection.length} selected code${selection.length === 1 ? '' : 's'} will be added to this list.`
                            : 'You can add codes to this list after creating it.'}
                    </p>
                    <input
                        type="text"
                        className="commodity-picker-search"
                        placeholder="List name — e.g. Coffee specialists"
                        value={newListName}
                        autoFocus
                        maxLength={100}
                        onChange={(e) => setNewListName(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' && newListName.trim() && !savingList) createList();
                        }}
                    />
                    <div className="ccl-modal-actions">
                        <button
                            type="button"
                            className="btn btn-secondary"
                            onClick={() => setNewListOpen(false)}
                        >
                            Cancel
                        </button>
                        <button
                            type="button"
                            className="btn btn-primary"
                            disabled={!newListName.trim() || savingList}
                            onClick={createList}
                        >
                            {savingList ? 'Creating…' : 'Create list'}
                        </button>
                    </div>
                </div>
            </Modal>
        </PageShell>
    );
};

export default CommodityCodesPage;
